import { API_KEY, ApiKey, TokenType } from 'services';
export const DESKTOP_API_HOST_KEY = '__desktop_api_host';
export const DESKTOP_ACCOUNT_KEY = '__desktop_account';
export const getStoredDesktopApiHost = ()=>{
    try {
        return localStorage.getItem(DESKTOP_API_HOST_KEY);
    } catch (unused) {
        return null;
    }
};
export const setStoredDesktopApiHost = (host)=>{
    const normalized = host.replace(/\/$/, '');
    localStorage.setItem(DESKTOP_API_HOST_KEY, normalized);
};
export const clearStoredDesktopApiHost = ()=>{
    localStorage.removeItem(DESKTOP_API_HOST_KEY);
};
export const getStoredDesktopAccount = ()=>{
    try {
        return localStorage.getItem(DESKTOP_ACCOUNT_KEY);
    } catch (unused) {
        return null;
    }
};
export const setStoredDesktopAccount = (account)=>{
    const slug = account.trim().replace(/^https?:\/\//i, '').replace(/\.api\.rocketlane\.com$/i, '').replace(/\.rocketlane\.com$/i, '').replace(/\.nitro\.run$/i, '').split('/')[0].split('.')[0];
    if (slug) {
        localStorage.setItem(DESKTOP_ACCOUNT_KEY, slug);
    }
};
export const clearStoredDesktopAccount = ()=>{
    localStorage.removeItem(DESKTOP_ACCOUNT_KEY);
};
/**
 * Desktop session = API key present.
 * API host comes from `__desktop_api_host` (tenant, e.g. goat.api.rocketlane.com)
 * after deep-link redeem; see getCurrentApiHost() in environment.ts.
 */ export const hasDesktopSession = ()=>{
    try {
        return localStorage.getItem(API_KEY) != null;
    } catch (unused) {
        return false;
    }
};
/**
 * Persist API key in the same shape the web app expects:
 * `['api_key', value, createdBy, keyId]`
 */ export const setDesktopApiKey = (apiKey)=>{
    localStorage.setItem(API_KEY, JSON.stringify([
        'api_key',
        apiKey,
        0,
        0
    ]));
};
// rocketlane:// is a public custom scheme any site can launch, so apiHost/account
// params are untrusted input — only ever point the desktop app's API host at our
// own tenant domains, never at whatever a crafted deep link supplies.
const ALLOWED_API_HOST_RE = /^([a-z0-9-]+\.)?api\.(rocketlane\.com|nitro\.run)$/i;
const ALLOWED_ACCOUNT_SLUG_RE = /^[a-z0-9-]+$/i;
const applyDesktopAccountAndHost = (params)=>{
    var _ref, _params_get, _ref1, _params_get1;
    const rawAccount = (_ref = (_params_get = params.get('account')) !== null && _params_get !== void 0 ? _params_get : params.get('subdomain')) !== null && _ref !== void 0 ? _ref : params.get('company');
    const rawApiHost = (_ref1 = (_params_get1 = params.get('apiHost')) !== null && _params_get1 !== void 0 ? _params_get1 : params.get('api_host')) !== null && _ref1 !== void 0 ? _ref1 : params.get('domain');
    const account = rawAccount && ALLOWED_ACCOUNT_SLUG_RE.test(rawAccount) ? rawAccount : null;
    const apiHostname = rawApiHost === null || rawApiHost === void 0 ? void 0 : rawApiHost.replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
    const apiHost = apiHostname && ALLOWED_API_HOST_RE.test(apiHostname) ? `https://${apiHostname}` : null;
    let host = apiHost;
    if (!host && account) {
        host = `https://${account}.api.rocketlane.com`;
    }
    // Prefer tenant host from account (create-api-key 401s on central api.rocketlane.com).
    if (!host) {
        host = 'https://api.rocketlane.com';
    }
    if (account) {
        setStoredDesktopAccount(account);
    }
    setStoredDesktopApiHost(host);
};
/**
 * Apply a deep link that already carries an apiKey (legacy / email deep links).
 */ export const applyDesktopDeepLinkParams = (params)=>{
    var _ref, _params_get;
    const apiKey = (_ref = (_params_get = params.get('apiKey')) !== null && _params_get !== void 0 ? _params_get : params.get('api_key')) !== null && _ref !== void 0 ? _ref : params.get('key');
    if (!apiKey) {
        return false;
    }
    applyDesktopAccountAndHost(params);
    setDesktopApiKey(apiKey);
    return true;
};
const REDEEMED_MAGIC_TOKEN_KEY = '__desktop_redeemed_magic_token';
const getRedeemedMagicToken = ()=>{
    try {
        return sessionStorage.getItem(REDEEMED_MAGIC_TOKEN_KEY);
    } catch (unused) {
        return null;
    }
};
const markMagicTokenRedeemed = (token)=>{
    try {
        sessionStorage.setItem(REDEEMED_MAGIC_TOKEN_KEY, token);
    } catch (unused) {
    // ignore quota / private-mode failures
    }
};
/**
 * Redeem a magic-link JWT from rocketlane://auth?token=… into a desktop API-key session.
 *
 * Idempotent: cold-start handlers (get_current + delayed Rust re-emit) and the
 * post-success `location.replace('/')` remount can deliver the same URL twice.
 * Clearing an already-created key and re-POSTing a spent token yields 401.
 */ export const redeemDesktopMagicToken = async (params)=>{
    const token = params.get('token');
    if (!token) {
        return false;
    }
    // ApiKey.create() below needs the new host/account applied first — it reads
    // getCurrentApiHost() to know where to POST. But that write must not stick
    // if the redeem then fails, or a preserved (still-valid) session ends up
    // pointed at the wrong tenant/central host and every later call 401s.
    // Snapshot so a failure path can restore exactly what was there before.
    const previousHost = getStoredDesktopApiHost();
    const previousAccount = getStoredDesktopAccount();
    const restorePreviousHostAndAccount = ()=>{
        if (previousHost) {
            setStoredDesktopApiHost(previousHost);
        } else {
            clearStoredDesktopApiHost();
        }
        if (previousAccount) {
            setStoredDesktopAccount(previousAccount);
        } else {
            clearStoredDesktopAccount();
        }
    };
    applyDesktopAccountAndHost(params);
    // Same token already redeemed this session — do not clear + re-create.
    if (ApiKey.exists() && getRedeemedMagicToken() === token) {
        return true;
    }
    // Create the new session before touching the old one: ApiKey.create()
    // overwrites the stored key on success, so a stale/replayed/invalid token
    // that throws here leaves a still-working existing session untouched
    // instead of getting the user logged out for nothing.
    try {
        const created = await ApiKey.create(token, TokenType.Rocketlane, '');
        const apiKeyValue = created === null || created === void 0 ? void 0 : created[1];
        if (!apiKeyValue || !ApiKey.exists()) {
            restorePreviousHostAndAccount();
            return false;
        }
    } catch (unused) {
        restorePreviousHostAndAccount();
        return false;
    }
    markMagicTokenRedeemed(token);
    return true;
};
